import { getSplitIOClient } from "splitio-loader/src";

type Params = {
  flagName: string;
  env?: string;
};

export const getSplitIOFlag = ({ flagName, env }: Params) => {
  let flag = undefined;

  // gets the "treatment" from the server
  // this will default to "control" as that is the SplitIO default value
  const serverTreatment = getSplitIOClient()?.getTreatment(flagName);

  // Get localstorage item for any treatment
  // will return null if not defined
  // not allowed in Prod
  const isProd = env?.includes("prod");
  const localTreatment: string | null = !isProd
    ? localStorage.getItem("treatment-" + flagName)
    : null;

  // if there is a localStorage treatment, use that first
  if (localTreatment) {
    flag = localTreatment;
  } else {
    flag = serverTreatment;
  }

  return flag;
};

export type Flag =
  | "fe-enable-ecp" // only flag to make it to prod
  | "settings-segments"
  | "q3-bank-account-profiles"
  | "q4-surcharge-feature"
  | "q3-msthps-1117-8471"
  | "monitor-diu-removal"
  | "field-frame-bundle"
  | "apple-pay-latest-sdk"
  | "q4-msthps-1980-3ds-pbl"
  | "q4-2024-authorized-domains"
  | "q1-2025-pbl-enhancements"
  | "partial-auth-q1-2025"
  | "q1-2025-pci-expiry"
  | "msthps-2459-shield"
  | "q1-2025-languages"
  | "msthps-2865-onboard-settings"
  | "co-branded-cards"
  | "additional-logging";

export const PRODUCTION_FLAGS = new Set<Flag>([
  // only when UAT, PVT, and has all RTL sign-offs
  // Playwright will check if there is values here and will run a
  // Suite of test with all flags off except PRODUCTION_FLAGS in every env
  // deployment
  "fe-enable-ecp", // Not used but only prod flag that is on
  "q3-bank-account-profiles",
  "settings-segments",
  "apple-pay-latest-sdk", // Planned RTL Together Q2-2025
  "q3-msthps-1117-8471", // Planned RTL Together Q2-2025 (Apple DIU Domains)
  "msthps-2459-shield",
]);

// Allowed flags: Set of flags that are ready to be tested or have been tested
export const TEST_READY_OR_COMPLETE = new Set<Flag>([
  ...PRODUCTION_FLAGS,
  "monitor-diu-removal",
  "field-frame-bundle",
  "q4-2024-authorized-domains",
  "q4-surcharge-feature",
  "q1-2025-pbl-enhancements",
  "q4-msthps-1980-3ds-pbl",
  "q1-2025-pci-expiry",
  "q1-2025-languages",
]);

// Non allowed flags: Not ready to be tested or have not been tested
export const IN_DEVELOPMENT_FLAGS = new Set<Flag>([
  ...TEST_READY_OR_COMPLETE,
  "q4-surcharge-feature",
  "partial-auth-q1-2025",
  "msthps-2865-onboard-settings",
  "co-branded-cards",
  "additional-logging",
]);

export const isDevEnv = (env: string): boolean => {
  return env.includes("local") || env.includes("dev");
};

export const isTestEnvWithApprovedFlags = (
  env: string,
  flag: Flag
): boolean => {
  return (
    (env.includes("test") || env.includes("cat")) &&
    TEST_READY_OR_COMPLETE.has(flag)
  );
};

export const isProductionWithApprovedFlags = (
  env: string,
  flag: Flag
): boolean => {
  return env.includes("prod") && PRODUCTION_FLAGS.has(flag);
};

// Feature flag check
// If environment is local or dev or an approved flag go to splitIO
// If flag is not approved for higher environments, return false
// Move flag from non-allowed into allowed once ready
export const getIsFeatureEnabled = (
  env: string = "",
  flag: Flag | "" = ""
): boolean => {
  if (env === "" || flag === "") {
    return false;
  }

  if (
    isDevEnv(env) ||
    isTestEnvWithApprovedFlags(env, flag) ||
    isProductionWithApprovedFlags(env, flag)
  ) {
    return getSplitIOFlag({ flagName: flag, env }) === "on";
  }

  return false;
};

// is SplitIO client ready -- returns a promise
export const getIsFeatureFlagServiceReady = () => {
  return getSplitIOClient()?.ready();
};
